import { useEffect, useRef, useState } from 'react';
import { cryptoStore } from '@/stores/crypto.store';
import { modalStore } from '@/stores/modal.store';
import { notify } from '@/utils/notify';
import AlertIcon from '@/components/Notifications/AlertIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import Loader from '@/components/Loader';
import { IWallet } from '@/utils/types/IWallet';

// Per-coin styling (background pill class + accent foreground colour for the logo)
const COIN_STYLE: Record<string, { bg: string; fg: string }> = {
    TRX: { bg: '#1a1a1d', fg: '#ff3a31' },
    USDT: { bg: '#1a1a1d', fg: '#26a17b' },
    BTC: { bg: '#1a1a1d', fg: '#f7931a' },
    ETH: { bg: '#1a1a1d', fg: '#c5c8d0' },
};

const COIN_NAME: Record<string, string> = {
    TRX: 'TRON',
    USDT: 'TETHER',
    BTC: 'BITCOIN',
    ETH: 'ETHEREUM',
};

const COIN_SVG: Record<string, JSX.Element> = {
    TRX: (
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 4l18 4-8 13L3 4zm3.4 1.6L13 17l5.4-9-12-2.4z" />
        </svg>
    ),
    USDT: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <circle cx="12" cy="12" r="9" />
            <path d="M8 8h8M12 8v9" />
            <path d="M9 11c0 1 1.5 1.6 3 1.6s3-.6 3-1.6-1.5-1.6-3-1.6" />
        </svg>
    ),
    BTC: (
        <svg viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="9" fill="currentColor" />
            <text
                x="12"
                y="16"
                textAnchor="middle"
                fontSize="13"
                fontWeight="900"
                fill="#1a1a1d"
                fontFamily="Segoe UI, sans-serif"
            >
                ₿
            </text>
        </svg>
    ),
    ETH: (
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l7 11-7 4-7-4 7-11zm0 14l7-4-7 10-7-10 7 4z" />
        </svg>
    ),
};

const CryptoPill = ({ code }: { code: string }) => {
    const style = COIN_STYLE[code] ?? COIN_STYLE.TRX;
    return (
        <span className="inline-flex items-center gap-2.5 text-sm font-semibold tracking-[0.02em] text-text_main">
            <span
                className="w-7 h-7 grid place-items-center rounded-full shrink-0"
                style={{ background: style.bg, color: style.fg }}
            >
                <span className="w-4 h-4 inline-flex items-center justify-center">
                    {COIN_SVG[code] ?? COIN_SVG.TRX}
                </span>
            </span>
            <span>{COIN_NAME[code] ?? code}</span>
        </span>
    );
};

const CryptoDeposit = () => {
    const { wallets, minAmountUsd } = cryptoStore((state) => state);
    const { setTitle, setComponent, setData } = modalStore((state) => state);

    const minDep = minAmountUsd > 0 ? minAmountUsd : 1;

    const [coin, setCoin] = useState<string>('');
    const [sum, setSum] = useState<string>(String(minDep));
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (wallets.length && !coin) setCoin(wallets[0].coinName);
    }, [wallets, coin]);

    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    const wallet: IWallet | undefined = wallets.find((w) => w.coinName === coin);

    function create() {
        const amount = Number(sum);
        if (!amount || amount < minDep) {
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper title="Error" text={`Min deposit is $${minDep}`} />
            );
            return;
        }
        if (!wallet) return;
        setTitle(`DEPOSIT ${coin}`);
        setComponent('Crypto address');
        setData({
            currency: coin,
            value: amount,
            wallet,
        });
    }

    if (!wallets.length) return <Loader />;

    return (
        <div className="flex flex-col gap-5 min-w-[380px] max-md:min-w-0">
            <div>
                <div className="font-chat-nick text-[11px] uppercase tracking-[0.14em] text-text_3 font-bold mb-2">
                    Select crypto
                </div>
                <div ref={wrapRef} className="relative">
                    <button
                        onClick={() => setOpen((v) => !v)}
                        className={`w-full flex items-center justify-between px-4 py-3.5 rounded-fl bg-layer_2 border transition-colors text-left ${
                            open
                                ? 'border-stroke_strong bg-layer_3'
                                : 'border-stroke hover:bg-layer_3 hover:border-stroke_strong'
                        }`}
                    >
                        <CryptoPill code={coin || (wallets[0]?.coinName ?? 'TRX')} />
                        <svg
                            viewBox="0 0 24 24"
                            className={`w-4 h-4 stroke-text_3 fill-none [stroke-width:2.2] [stroke-linecap:round] [stroke-linejoin:round] transition-transform ${
                                open ? 'rotate-180' : ''
                            }`}
                        >
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>
                    {open && (
                        <div
                            className="absolute left-0 right-0 top-full mt-1.5 rounded-fl border border-stroke_strong overflow-hidden z-20 fluent-scrollbar max-h-72 overflow-y-auto"
                            style={{
                                background: 'rgba(28,30,38,0.98)',
                                boxShadow: '0 16px 36px rgba(0,0,0,0.55)',
                            }}
                        >
                            {wallets.map((w) => {
                                const selected = w.coinName === coin;
                                return (
                                    <button
                                        key={w.id}
                                        onClick={() => {
                                            setCoin(w.coinName);
                                            setOpen(false);
                                        }}
                                        className={`w-full flex items-center px-4 py-3 transition-colors text-left ${
                                            selected
                                                ? 'bg-layer_3 shadow-[inset_3px_0_0_var(--p2)]'
                                                : 'hover:bg-layer_3'
                                        }`}
                                        style={selected ? { boxShadow: 'inset 3px 0 0 #4cc2ff' } : undefined}
                                    >
                                        <CryptoPill code={w.coinName} />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div>
                <div className="font-chat-nick text-[11px] uppercase tracking-[0.14em] text-text_3 font-bold mb-2">
                    Sum
                </div>
                <input
                    type="number"
                    min={minDep}
                    step={1}
                    value={sum}
                    onChange={(e) => setSum(e.target.value)}
                    className="w-full bg-layer_2 border border-stroke focus:border-accent focus:bg-layer_3 rounded-fl px-4 py-3 text-[22px] font-bold text-text_main outline-none tabular-nums transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                <div className="text-right text-xs text-text_3 mt-2">
                    Min deposit:{' '}
                    <b className="font-bold" style={{ color: '#5fe0ff' }}>
                        ${minDep}
                    </b>
                </div>
            </div>

            <button
                onClick={create}
                className="group relative overflow-hidden w-full py-3.5 rounded-fl font-chat-nick text-[13px] font-extrabold tracking-[0.08em] text-white hover:-translate-y-0.5 transition-transform"
                style={{
                    background: 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                    boxShadow:
                        'inset 0 1px 0 rgba(255,255,255,0.35), 0 0 22px -4px rgba(139,109,255,0.7), 0 8px 20px -6px rgba(58,214,255,0.5)',
                }}
            >
                <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 -translate-x-[120%] group-hover:translate-x-[120%] transition-transform duration-[700ms] ease-out"
                    style={{
                        background:
                            'linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.5), transparent 70%)',
                    }}
                />
                <span className="relative">CREATE DEPOSIT</span>
            </button>
        </div>
    );
};

export default CryptoDeposit;
